import { readXlsxMatrices } from '@obraplan/table-reader/xlsx-matrix';
import { looksLikeZip } from '@obraplan/table-reader/xlsx-reader';
import {
  PARK_EUROPA_DE_PARA,
  detectScheduleFileKind,
  readGanttProTasks,
  readScheduleItemTemplate,
  resolveGanttProTasks,
  type DeParaOverride,
  type GanttProTask,
  type Matrix,
  type PendingApproval,
  type RowIssue,
  type ScheduleFileKind,
  type ScheduleItemRowData,
  type UnresolvedName,
} from '@obraplan/core/domain/schedule/ganttpro/index';
import type {
  ScheduleItemRowIssue,
  ScheduleItemRowStatus,
  ScheduleItemsImportPreview,
} from '@obraplan/core/application/contracts/schedule-items';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';

/**
 * O arquivo do cronograma até as linhas de item (Fase 25, ADR-161).
 *
 * A importação tem duas metades. A deste módulo roda **no navegador** e não grava nada:
 * abre o `.xlsx`, descobre se é o GanttPRO ou o modelo do sistema pelo cabeçalho, lê as
 * tarefas e aplica o de-para. A outra metade — conferir contra o cadastro e gravar — é do
 * servidor, que só recebe linhas normalizadas.
 *
 * Duas fases de propósito: `loadScheduleFile` abre o arquivo **uma vez**; `prepareImport`
 * resolve as tarefas e é barata, para rodar de novo a cada escolha do usuário na
 * conferência (os nomes que o de-para não conhecia).
 */

/** Acima disto o servidor recusa a importação (`previewScheduleItemsImportSchema`). */
export const MAX_IMPORT_ROWS = 5000;

/** A aba do modelo do sistema que tem os itens. */
const TEMPLATE_SHEET = 'itens';

interface FileBase {
  readonly fileName: string;
  readonly sheetName: string;
  /** Problemas de linha que a leitura achou — o arquivo continua valendo. */
  readonly readIssues: readonly RowIssue[];
}

export type LoadedScheduleFile =
  | (FileBase & {
      readonly kind: 'GANTTPRO';
      readonly tasks: readonly GanttProTask[];
      /** O nome do grupo raiz da obra ("TORRE 2"), se houver — sugere o nome da torre. */
      readonly rootName: string | undefined;
    })
  | (FileBase & {
      readonly kind: 'TEMPLATE';
      readonly rows: readonly ScheduleItemRowData[];
    });

export const FILE_KIND_LABEL: Readonly<Record<ScheduleFileKind, string>> = {
  GANTTPRO: 'Cronograma do GanttPRO',
  TEMPLATE: 'Modelo de cronograma por mão de obra',
};

/**
 * Abre o `.xlsx` e lê o que ele é. O erro é uma frase para a tela, em português.
 *
 * A aba "Itens" vence quando o arquivo é o modelo; senão vale a primeira aba que tenha o
 * cabeçalho de um dos dois formatos.
 */
export function loadScheduleFile(
  bytes: Uint8Array,
  fileName: string,
): Result<LoadedScheduleFile, string> {
  if (!looksLikeZip(bytes)) {
    return err('Este arquivo não é uma planilha .xlsx. Exporte o cronograma do GanttPRO em Excel.');
  }

  let sheets: ReturnType<typeof readXlsxMatrices>;
  try {
    sheets = readXlsxMatrices(bytes);
  } catch {
    return err('Não consegui abrir a planilha: o arquivo parece estar corrompido.');
  }

  const detected = sheets
    .map((sheet) => ({ sheet, kind: detectScheduleFileKind(sheet.matrix) }))
    .filter(
      (found): found is { sheet: (typeof sheets)[number]; kind: ScheduleFileKind } =>
        found.kind !== undefined,
    );
  const chosen =
    detected.find(
      (found) =>
        found.kind === 'TEMPLATE' && normalizeName(found.sheet.sheetName) === TEMPLATE_SHEET,
    ) ?? detected[0];

  if (chosen === undefined) {
    return err(
      'Não reconheci o formato. O arquivo precisa ser o cronograma exportado do GanttPRO ' +
        '(colunas "Número de EDT" e "Nome da tarefa") ou o modelo de cronograma por mão de obra ' +
        '(aba "Itens").',
    );
  }

  return loadMatrix(chosen.sheet.matrix, chosen.kind, chosen.sheet.sheetName, fileName);
}

/** A matriz já aberta até o arquivo lido — separado para o teste não precisar de bytes. */
export function loadMatrix(
  matrix: Matrix,
  kind: ScheduleFileKind,
  sheetName: string,
  fileName: string,
): Result<LoadedScheduleFile, string> {
  if (kind === 'GANTTPRO') {
    const reading = readGanttProTasks(matrix);
    if (!reading.ok) return err(reading.error.message);
    const root = reading.value.tasks.find((task) => task.isGroup && task.groupPath.length === 0);
    return ok({
      kind,
      fileName,
      sheetName,
      readIssues: reading.value.issues,
      tasks: reading.value.tasks,
      rootName: root?.name,
    });
  }

  const reading = readScheduleItemTemplate(matrix);
  if (!reading.ok) return err(reading.error.message);
  return ok({
    kind,
    fileName,
    sheetName,
    readIssues: reading.value.issues,
    rows: reading.value.rows,
  });
}

/** "TORRE 2" quando a raiz do arquivo diz isso; senão vazio, e o usuário digita. */
export function suggestedTowerName(file: LoadedScheduleFile): string {
  if (file.kind !== 'GANTTPRO') return '';
  return file.rootName !== undefined && /^torre\b/i.test(file.rootName) ? file.rootName : '';
}

/** Quantas tarefas e grupos o arquivo tem, para a tela dizer o que leu. */
export function describeFile(file: LoadedScheduleFile): string {
  if (file.kind === 'TEMPLATE') {
    return `${String(file.rows.length)} ${file.rows.length === 1 ? 'item' : 'itens'}`;
  }
  const groups = file.tasks.filter((task) => task.isGroup).length;
  const leaves = file.tasks.length - groups;
  return `${String(leaves)} tarefas e ${String(groups)} grupos`;
}

// ── Das tarefas às linhas ──────────────────────────────────────────────────────────

export interface PreparedImport {
  /** As linhas que o servidor recebe, na ordem do arquivo. */
  readonly rows: readonly ScheduleItemRowData[];
  /** Nomes que o de-para não conhece: a conferência pede a escolha (só no GanttPRO). */
  readonly unresolved: readonly UnresolvedName[];
  /** Tarefas que o de-para conhece mas que não puderam virar item, e as linhas ilegíveis. */
  readonly issues: readonly RowIssue[];
  /** Serviços que o de-para usa e que esperam a aprovação do usuário (D3). */
  readonly pendingApproval: readonly PendingApproval[];
}

/**
 * Resolve o arquivo em linhas de item. Pura: o mesmo arquivo, a mesma torre e as mesmas
 * escolhas dão sempre as mesmas linhas.
 *
 * No modelo, a torre vem em cada linha e as escolhas não se aplicam — não há nome
 * desconhecido, o próprio arquivo já diz disciplina e serviço.
 */
export function prepareImport(
  file: LoadedScheduleFile,
  towerName: string,
  overrides: Readonly<Record<string, DeParaOverride>>,
): PreparedImport {
  if (file.kind === 'TEMPLATE') {
    return { rows: file.rows, unresolved: [], issues: file.readIssues, pendingApproval: [] };
  }

  const resolution = resolveGanttProTasks(file.tasks, {
    towerName: towerName.trim(),
    dePara: PARK_EUROPA_DE_PARA,
    overrides,
  });
  return {
    rows: resolution.rows,
    unresolved: resolution.unresolved,
    issues: [...file.readIssues, ...resolution.issues],
    pendingApproval: resolution.pendingApproval,
  };
}

/** A chave de um serviço no cadastro: disciplina e nome, sem caixa nem espaço repetido. */
export function serviceKey(disciplineName: string, serviceName: string): string {
  return `${normalizeName(disciplineName)}|${normalizeName(serviceName)}`;
}

/**
 * Tira as linhas dos serviços que o usuário **não aprovou** (D3).
 *
 * A aprovação é um a um: o serviço novo só entra se a caixa dele estiver marcada, e as
 * linhas dos que ficaram de fora nem chegam ao servidor — não viram erro, simplesmente não
 * participam. A linha de frente inteira (sem serviço) nunca é cortada por isto.
 */
export function withoutDeclinedServices(
  rows: readonly ScheduleItemRowData[],
  declined: ReadonlySet<string>,
): ScheduleItemRowData[] {
  if (declined.size === 0) return [...rows];
  return rows.filter(
    (row) =>
      row.serviceName === null || !declined.has(serviceKey(row.disciplineName, row.serviceName)),
  );
}

// ── A prévia do servidor, linha a linha ─────────────────────────────────────────────

export interface PreviewLine {
  readonly index: number;
  readonly row: ScheduleItemRowData;
  readonly status: ScheduleItemRowStatus;
  readonly issues: readonly ScheduleItemRowIssue[];
  /** O serviço da linha não foi aprovado: ela não vai ao servidor na confirmação. */
  readonly excluded: boolean;
}

/**
 * Junta cada linha enviada à resposta da prévia.
 *
 * O servidor devolve uma resposta por linha, **na mesma ordem** em que recebeu — e é por
 * posição que se junta, porque uma tarefa do GanttPRO vira vários itens que repartem o
 * mesmo número de linha do arquivo. `undefined` quando os tamanhos divergem (uma resposta
 * velha para linhas novas): a tela espera a resposta certa em vez de mostrar uma mentira.
 */
export function joinPreview(
  rows: readonly ScheduleItemRowData[],
  preview: ScheduleItemsImportPreview,
  declined: ReadonlySet<string>,
): PreviewLine[] | undefined {
  if (preview.rows.length !== rows.length) return undefined;
  return rows.map((row, index) => {
    const answer = preview.rows[index]!;
    return {
      index,
      row,
      status: answer.status,
      issues: answer.issues,
      excluded:
        row.serviceName !== null && declined.has(serviceKey(row.disciplineName, row.serviceName)),
    };
  });
}

export interface LineCounts {
  readonly create: number;
  readonly update: number;
  readonly unchanged: number;
  readonly error: number;
  /** Linhas de serviço não aprovado — contadas à parte, e fora dos quatro de cima. */
  readonly excluded: number;
}

export function countLines(lines: readonly PreviewLine[]): LineCounts {
  const counts = { create: 0, update: 0, unchanged: 0, error: 0, excluded: 0 };
  for (const line of lines) {
    if (line.excluded) counts.excluded += 1;
    else if (line.status === 'CREATE') counts.create += 1;
    else if (line.status === 'UPDATE') counts.update += 1;
    else if (line.status === 'UNCHANGED') counts.unchanged += 1;
    else counts.error += 1;
  }
  return counts;
}

export const LINE_STATUS_LABEL: Readonly<Record<ScheduleItemRowStatus, string>> = {
  CREATE: 'Criar',
  UPDATE: 'Atualizar',
  UNCHANGED: 'Igual',
  ERROR: 'Erro',
};
